import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  softwareUpdateRolloutService,
  type Rollout,
  type RolloutPackage,
  type RolloutTarget,
  type RolloutTargetOptions,
  type PackageSlot,
  type CreateRolloutRequest,
  type UpdateRolloutRequest,
  type ListRolloutsParams,
  type ListPackageSlotsParams,
  type RolloutStatus,
  type RolloutType,
  type ListRolloutEventsParams,
  type ListRolloutEventsTimeseriesParams,
  type ListDeviceSoftwareUpdateParams,
} from '@/lib/software-update-rollout.service';

export const softwareUpdateRolloutKeys = {
  all: ['software-update-rollouts'] as const,
  lists: () => [...softwareUpdateRolloutKeys.all, 'list'] as const,
  list: (params: ListRolloutsParams) =>
    [...softwareUpdateRolloutKeys.lists(), params] as const,
  packageSlots: (params: ListPackageSlotsParams) =>
    [...softwareUpdateRolloutKeys.all, 'package-slots', params] as const,
  targetOptions: () => [...softwareUpdateRolloutKeys.all, 'target-options'] as const,
  details: () => [...softwareUpdateRolloutKeys.all, 'detail'] as const,
  detail: (id: string) => [...softwareUpdateRolloutKeys.details(), id] as const,
  detailV2: (id: string) => [...softwareUpdateRolloutKeys.detail(id), 'v2'] as const,
  events: (id: string, params: ListRolloutEventsParams) =>
    [...softwareUpdateRolloutKeys.detail(id), 'events', params] as const,
  eventsTimeseries: (id: string, params: ListRolloutEventsTimeseriesParams) =>
    [...softwareUpdateRolloutKeys.detail(id), 'events-timeseries', params] as const,
  deviceSoftwareUpdate: (deviceId: string, params: ListDeviceSoftwareUpdateParams) =>
    ['device-software-update', deviceId, params] as const,
};

export function useSoftwareUpdateRollouts(params: ListRolloutsParams = {}) {
  return useQuery({
    queryKey: softwareUpdateRolloutKeys.list(params),
    queryFn: () => softwareUpdateRolloutService.list(params),
  });
}

export function useSoftwareUpdatePackageSlots(params: ListPackageSlotsParams) {
  return useQuery({
    queryKey: softwareUpdateRolloutKeys.packageSlots(params),
    queryFn: () => softwareUpdateRolloutService.listPackageSlots(params),
    enabled: !!params.type,
  });
}

export function useRolloutTargetOptions() {
  return useQuery({
    queryKey: softwareUpdateRolloutKeys.targetOptions(),
    queryFn: () => softwareUpdateRolloutService.getRolloutTargetOptions(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSoftwareUpdateRollout(id: string) {
  return useQuery({
    queryKey: softwareUpdateRolloutKeys.detail(id),
    queryFn: () => softwareUpdateRolloutService.get(id),
    enabled: !!id,
  });
}

export function useCreateSoftwareUpdateRollout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRolloutRequest) => softwareUpdateRolloutService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateRolloutKeys.lists() });
    },
  });
}

export function useUpdateSoftwareUpdateRollout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRolloutRequest }) =>
      softwareUpdateRolloutService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateRolloutKeys.lists() });
      queryClient.invalidateQueries({ queryKey: softwareUpdateRolloutKeys.detail(variables.id) });
    },
  });
}

export function useDeleteSoftwareUpdateRollout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => softwareUpdateRolloutService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateRolloutKeys.lists() });
    },
  });
}

export function useDeployRollout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => softwareUpdateRolloutService.deploy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateRolloutKeys.all });
    },
  });
}

export function useArchiveRollout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => softwareUpdateRolloutService.archive(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateRolloutKeys.all });
      queryClient.invalidateQueries({ queryKey: softwareUpdateRolloutKeys.detail(id) });
    },
  });
}

export function useDeployOSRecoveryRollout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => softwareUpdateRolloutService.deployOSRecovery(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateRolloutKeys.all });
    },
  });
}

export function useRolloutDetail(id: string) {
  return useQuery({
    queryKey: softwareUpdateRolloutKeys.detailV2(id),
    queryFn: () => softwareUpdateRolloutService.getDetail(id),
    enabled: !!id,
  });
}

export function useRolloutEvents(id: string, params: ListRolloutEventsParams = {}) {
  return useQuery({
    queryKey: softwareUpdateRolloutKeys.events(id, params),
    queryFn: () => softwareUpdateRolloutService.listEvents(id, params),
    enabled: !!id,
  });
}

export function useRolloutEventsTimeseries(
  id: string,
  params: ListRolloutEventsTimeseriesParams,
) {
  return useQuery({
    queryKey: softwareUpdateRolloutKeys.eventsTimeseries(id, params),
    queryFn: () => softwareUpdateRolloutService.getEventsTimeseries(id, params),
    enabled: !!id && !!params.since && !!params.until,
  });
}

export function useDeviceSoftwareUpdate(
  deviceId: string,
  params: ListDeviceSoftwareUpdateParams = {},
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: softwareUpdateRolloutKeys.deviceSoftwareUpdate(deviceId, params),
    queryFn: () => softwareUpdateRolloutService.getDeviceSoftwareUpdate(deviceId, params),
    enabled: !!deviceId,
    refetchInterval: options.refetchInterval,
  });
}

export type {
  Rollout,
  RolloutPackage,
  RolloutTarget,
  RolloutTargetOptions,
  PackageSlot,
  CreateRolloutRequest,
  UpdateRolloutRequest,
  RolloutStatus,
  RolloutType,
  ListRolloutsParams,
  ListRolloutEventsParams,
  ListRolloutEventsTimeseriesParams,
  ListDeviceSoftwareUpdateParams,
};
